import { Fragment, ReactNode } from 'react'
import classnames from 'classnames/bind'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

// Components
import Shell from '../Shell'
import { DocumentIcon } from '../Icons'

// Methods
import { helpArticlePath } from '../../methods'

// Types
import type { HelpSearchItem } from '@/api/searchHelpArticles'

// Styles
import styles from '../../style.module.scss'

const cx = classnames.bind(styles)

type Props = {
  keyword: string
  results: HelpSearchItem[]
  countryCode: string
  countryQuery?: string
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** 標出關鍵字（不分大小寫） */
const highlight = (text: string, keyword: string): ReactNode => {
  if (!keyword) return text
  const parts = text.split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === keyword.toLowerCase() ? <mark key={`${i}-${part}`}>{part}</mark> : <Fragment key={`${i}-${part}`}>{part}</Fragment>,
  )
}

const HelpSearch = ({ keyword, results, countryCode, countryQuery }: Props) => {
  const { t } = useTranslation()

  return (
    <Shell keyword={keyword} countryCode={countryCode} countryQuery={countryQuery}>
      {!keyword ? (
        <p className={cx('help-empty')}>{t('Help_Center_Search_Hint')}</p>
      ) : (
        <>
          <h1 className={cx('help-search-summary')}>{t('Help_Center_Search_Result', { q: keyword, count: results.length })}</h1>
          {results.length === 0 ? (
            <p className={cx('help-empty')}>{t('Help_Center_Search_Empty', { q: keyword })}</p>
          ) : (
            <ul className={cx('help-card', 'help-search-results')}>
              {results.map((item) => (
                <li key={item.id}>
                  <Link className={cx('help-search-results__item')} href={helpArticlePath(item.id, countryQuery)}>
                    <span className={cx('help-article-list__icon')}>
                      <DocumentIcon />
                    </span>
                    <span className={cx('help-search-results__text')}>
                      <span className={cx('help-search-results__title')}>{highlight(item.title, keyword)}</span>
                      {item.snippet && <span className={cx('help-search-results__snippet')}>{highlight(item.snippet, keyword)}</span>}
                      <span className={cx('help-search-results__collection')}>{item.collectionName}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Shell>
  )
}

export default HelpSearch
